import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { 
  Send, X, MessageCircle, Circle, Loader2, Check, CheckCheck,
  User, Clock
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Get WebSocket URL from API URL
const getWsUrl = () => {
  const url = new URL(API_URL);
  const protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${protocol}//${url.host}/api/chat/ws`;
};

export default function LiveChat({ 
  inquiryId, 
  currentUserId, 
  otherPartyName,
  otherPartyId,
  productName,
  onClose,
  isOpen 
}) {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [isConnected, setIsConnected] = useState(false);
  const [isOtherOnline, setIsOtherOnline] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  
  const wsRef = useRef(null);
  const messagesEndRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Load chat history
  const loadMessages = useCallback(async () => {
    try {
      const token = localStorage.getItem('atff_token');
      const res = await fetch(`${API_URL}/api/chat/messages/${inquiryId}?limit=100`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setMessages(data.messages || []);
      }
    } catch (error) {
      console.error('Failed to load messages:', error);
    } finally {
      setLoading(false);
    }
  }, [inquiryId]);

  // WebSocket connection
  const connectWebSocket = useCallback(() => {
    if (!currentUserId || !inquiryId) return;
    
    // Get token for WebSocket authentication
    const token = localStorage.getItem('atff_token');
    const wsUrl = `${getWsUrl()}/${currentUserId}?token=${encodeURIComponent(token || '')}`;
    
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onopen = () => {
      
      setIsConnected(true);
      
      // Join the inquiry room
      ws.send(JSON.stringify({
        type: 'join_inquiry',
        inquiry_id: inquiryId
      }));
      
      // Check if other party is online
      ws.send(JSON.stringify({
        type: 'check_online',
        user_id: otherPartyId
      }));
      
      // Mark messages as read
      ws.send(JSON.stringify({
        type: 'mark_read',
        inquiry_id: inquiryId
      }));
    };

    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);
      
      
      switch (data.type) {
        case 'new_message':
          setMessages(prev => [...prev, data.message]);
          // Mark as read immediately since chat is open
          ws.send(JSON.stringify({
            type: 'mark_read',
            inquiry_id: inquiryId
          }));
          break;
        
        case 'message_sent':
          // Update message in list with confirmation
          setMessages(prev => {
            const exists = prev.some(m => m.message_id === data.message.message_id);
            if (!exists) {
              return [...prev, data.message];
            }
            return prev;
          });
          setSending(false);
          break;
        
        case 'typing':
          if (data.inquiry_id === inquiryId) {
            setIsTyping(true);
          }
          break;
        
        case 'stop_typing':
          if (data.inquiry_id === inquiryId) {
            setIsTyping(false);
          }
          break;
        
        case 'user_online':
          if (data.user_id === otherPartyId) {
            setIsOtherOnline(true);
          }
          break;
        
        case 'user_offline':
          if (data.user_id === otherPartyId) {
            setIsOtherOnline(false);
          }
          break;
        
        case 'online_status':
          if (data.user_id === otherPartyId) {
            setIsOtherOnline(data.online);
          }
          break;
        
        case 'messages_read':
          // Update read status for our messages
          setMessages(prev => prev.map(m => 
            m.sender_id === currentUserId ? { ...m, read: true } : m
          ));
          break;
        
        case 'error':
          console.error('WebSocket error:', data.message);
          break;
        
        default:
          break;
      }
    };

    ws.onclose = () => {
      
      setIsConnected(false);
      
      // Attempt to reconnect after 3 seconds
      reconnectTimeoutRef.current = setTimeout(() => {
        if (isOpen) {
          connectWebSocket();
        }
      }, 3000);
    };

    ws.onerror = (error) => {
      console.error('WebSocket error:', error);
      setIsConnected(false);
    };

    return () => {
      if (ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({
          type: 'leave_inquiry',
          inquiry_id: inquiryId
        }));
        ws.close();
      }
    };
  }, [currentUserId, inquiryId, otherPartyId, isOpen]);

  useEffect(() => {
    if (isOpen && inquiryId) {
      loadMessages();
      const cleanup = connectWebSocket();
      
      return () => {
        if (cleanup) cleanup();
        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current);
        }
      };
    }
  }, [isOpen, inquiryId, loadMessages, connectWebSocket]);

  // Handle typing indicator
  const handleTyping = () => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'typing',
        inquiry_id: inquiryId
      }));
      
      // Clear existing timeout
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      
      // Stop typing after 2 seconds of no input
      typingTimeoutRef.current = setTimeout(() => {
        if (wsRef.current?.readyState === WebSocket.OPEN) {
          wsRef.current.send(JSON.stringify({
            type: 'stop_typing',
            inquiry_id: inquiryId
          }));
        }
      }, 2000);
    }
  };

  const sendMessage = (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      return;
    }
    
    setSending(true);
    wsRef.current.send(JSON.stringify({
      type: 'chat_message',
      inquiry_id: inquiryId,
      content: newMessage.trim()
    }));
    
    // Stop typing indicator
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    wsRef.current.send(JSON.stringify({
      type: 'stop_typing',
      inquiry_id: inquiryId
    }));
    
    setNewMessage('');
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    
    if (isToday) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' }) + ' ' + 
           date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed bottom-4 right-4 w-96 h-[500px] bg-white rounded-xl shadow-2xl border border-gray-200 flex flex-col z-50" data-testid="live-chat-widget">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b bg-green-700 text-white rounded-t-xl">
        <div className="flex items-center gap-3">
          <div className="relative">
            <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
              <User className="w-5 h-5" />
            </div>
            <Circle 
              className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 ${isOtherOnline ? 'text-green-400 fill-green-400' : 'text-gray-400 fill-gray-400'}`} 
            />
          </div>
          <div>
            <h3 className="font-semibold text-sm">{otherPartyName}</h3>
            <p className="text-xs text-green-100">
              {isOtherOnline ? 'Online' : 'Offline'} • {productName}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {!isConnected && (
            <Badge variant="outline" className="bg-yellow-500/20 text-yellow-100 border-yellow-400 text-xs">
              Reconnecting...
            </Badge>
          )}
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={onClose}
            className="text-white hover:bg-white/20 h-8 w-8 p-0"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <Loader2 className="w-6 h-6 animate-spin text-green-600" />
          </div>
        ) : messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-gray-500">
            <MessageCircle className="w-12 h-12 mb-2 opacity-50" />
            <p className="text-sm">No messages yet</p>
            <p className="text-xs">Start the conversation!</p>
          </div>
        ) : (
          <>
            {messages.map((msg, index) => {
              const isOwn = msg.sender_id === currentUserId;
              const showDate = index === 0 || 
                new Date(msg.created_at).toDateString() !== new Date(messages[index-1].created_at).toDateString();
              
              return (
                <React.Fragment key={msg.message_id}>
                  {showDate && (
                    <div className="flex justify-center my-2">
                      <span className="text-xs bg-gray-200 text-gray-600 px-3 py-1 rounded-full">
                        {new Date(msg.created_at).toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' })}
                      </span>
                    </div>
                  )}
                  <div className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[75%] ${isOwn ? 'order-2' : 'order-1'}`}>
                      <div 
                        className={`px-4 py-2 rounded-2xl ${
                          isOwn 
                            ? 'bg-green-600 text-white rounded-br-md' 
                            : 'bg-white text-gray-800 rounded-bl-md shadow-sm'
                        }`}
                      >
                        <p className="text-sm whitespace-pre-wrap break-words">{msg.content}</p>
                      </div>
                      <div className={`flex items-center gap-1 mt-1 ${isOwn ? 'justify-end' : 'justify-start'}`}>
                        <span className="text-[10px] text-gray-400">
                          {formatTime(msg.created_at)}
                        </span>
                        {isOwn && (
                          msg.read 
                            ? <CheckCheck className="w-3 h-3 text-blue-500" />
                            : <Check className="w-3 h-3 text-gray-400" />
                        )}
                      </div>
                    </div>
                  </div>
                </React.Fragment>
              );
            })}
            
            {/* Typing indicator */}
            {isTyping && (
              <div className="flex justify-start">
                <div className="bg-white px-4 py-2 rounded-2xl rounded-bl-md shadow-sm">
                  <div className="flex gap-1">
                    <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                    <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                    <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Input */}
      <form onSubmit={sendMessage} className="p-3 border-t bg-white rounded-b-xl">
        <div className="flex items-center gap-2">
          <Input
            value={newMessage}
            onChange={(e) => {
              setNewMessage(e.target.value);
              handleTyping();
            }}
            placeholder="Type a message..."
            className="flex-1 border-gray-200 focus:border-green-500"
            disabled={!isConnected}
            data-testid="chat-input"
          />
          <Button 
            type="submit" 
            size="sm"
            className="bg-green-600 hover:bg-green-700 h-10 w-10 p-0"
            disabled={!newMessage.trim() || !isConnected || sending}
            data-testid="chat-send-btn"
          >
            {sending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Send className="w-4 h-4" />
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}

// Chat button component to open chat
export function ChatButton({ inquiryId, currentUserId, otherPartyName, otherPartyId, productName, unreadCount = 0 }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <>
      <Button
        onClick={() => setIsOpen(true)}
        className="bg-green-600 hover:bg-green-700 relative"
        size="sm"
        data-testid={`open-chat-${inquiryId}`}
      >
        <MessageCircle className="w-4 h-4 mr-2" />
        Chat
        {unreadCount > 0 && (
          <Badge className="absolute -top-2 -right-2 bg-red-500 text-white text-xs h-5 w-5 flex items-center justify-center p-0 rounded-full">
            {unreadCount > 9 ? '9+' : unreadCount}
          </Badge>
        )}
      </Button>
      
      <LiveChat
        inquiryId={inquiryId}
        currentUserId={currentUserId}
        otherPartyName={otherPartyName}
        otherPartyId={otherPartyId}
        productName={productName}
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
      />
    </>
  );
}
